<template>
    <div class="pt-1" style="user-select: none; border-top: 1px solid #e1e1e1; font-size: .9em">
        <div
            :style="{width: textColWidthPercentage + '%', marginTop: '-2px', marginBottom: '-2px'}"
        >
            <!-- Data Row -->
            <div class="row" :class="{ 'cell-danger': !orderObject?.account || !orderObject?.invoicing_code || !firstItem }">
                <div class="col-3 nopads" style="font-size: .9em">
                    <div class="row align-items-center">
                        <div class="col-auto">
                            <font-awesome-icon
                                icon="caret-right"
                                class="pointer pt-2 px-0 mx-0"
                                style="outline: none; width: 1em; height: 1.1em"
                                :class="{ 'fa-rotate-90': itemsVisible }"
                                @click.stop="toggleItems"
                            />
                        </div>

                        <div class="col-auto">
                            <input
                                class="form-check-input"
                                style="font-size: 1.2em"
                                type="checkbox"
                                id="copyOrders"
                                name="copy-orders"
                                :checked="isSelected"
                                @change="toggleSelection"
                            />
                        </div>

                        <div
                            class="col pointer"
                            :id="'o_t'+orderObject?.id"
                            @click.stop="$emit('showOrder', orderObject)"
                        >
                            {{ orderObject?.id }} - {{ orderObject && getOrderAndExecutorStr(orderObject) }}
                            <span v-if="orderObject?.info" class="text-muted"> — {{ truncateInfo(orderObject.info) }}</span>
                            <span v-if="(order.items || []).length > 1" class="text-muted"> ({{ order.items.length }})</span>
                        </div>
                    </div>

                </div>
                <div class="col-2">
                    <span class="ml-2 pointer">
                      {{ (rowStartDate ? rowStartDate.substring(0, 10) : $t('common.unknown')) + " - " + (rowEndDate ? rowEndDate.substring(0, 10) : $t('common.unknown')) }}
                    </span>
                </div>
                <div class="col-2">
                    <span
                        class="ml-2 pointer"
                        :class="{ 'underline-text': !orderObject?.account }"
                        @click.stop="$emit('showOrder', orderObject)"
                    >
                        {{ orderObject?.account ? orderObject.account.code + ' - ' + orderObject.account.name : $t('account.set_account') }}
                    </span>
                </div>
                <div class="col-1">
                    <span
                        class="ml-2 pointer"
                        :class="{ 'underline-text': !orderObject?.invoicing_code }"
                        @click.stop="$emit('showOrder', orderObject)"
                    >
                      {{ orderObject?.invoicing_code ? orderObject.invoicing_code.code + ' - ' + orderObject.invoicing_code.name : $t('account.set_code') }}
                    </span>
                </div>
                <div class="col-1">
                    <span class="ml-2 pointer">
                      {{ firstItem?.pricing_model ? pricingModelToText(firstItem.pricing_model, null) : "-" }}
                    </span>
                </div>
                <div class="col-1">
                    <span class="ml-2 pointer">
                      {{ firstItem?.unit_price ? (firstItem.unit_price / 100).toFixed(3) : null }}
                    </span>
                </div>
                <div class="col-1">
                    <span class="ml-2 pointer">
                      {{ rowAmount.toFixed(2) }}
                    </span>
                </div>
                <div class="col-1">
                    <span class="ml-2 pointer">
                      {{ rowCost.toFixed(2) }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Collapsible section (standard Bootstrap) -->
        <div v-if="itemsVisible && summaries.length > 0" class="col-sm-12 px-3 mt-1  pb-2 mb-2" style="border: 2px #ddecfa solid; border-radius: .5em;">
            <contract-trip-summary-list
                :summaries="summaries"
                :invoice-state-options="invoiceStateOptions"
                :user="user"
                :invoicing-fields-default="true"
                :hide-excel-button="true"
                :hide-detail-button="true"
                :table-height="'100%'"
                :small-text="true"
                @update-request="showInvoiceSummary"
                @row-selected="emitTripDetails"
                @delivery-tons-clicked="emitTripDeliveries"
                @invoice-history-clicked="emitInvoiceHistories"
                @contract-clicked="emitContractDetails"
                @order-clicked="emitTripOrder"
            />
        </div>
    </div>
</template>


<script>
import {timeUtils} from "@/components/mixins/TimeUtils";
import {restApi} from "@/components/mixins/RestApiMixin";
import {orderMixin} from "@/components/mixins/OrderMixin";
import {priceHelper} from '@/components/mixins/PricingMixin'
import ContractTripSummaryList from "@/components/tripsummary/ContractTripSummaryList.vue";
import {inject} from "vue";
export default {
    name: 'InvoicingOrderItem',
    components: {
        ContractTripSummaryList
    },
    mixins: [timeUtils, restApi, orderMixin, priceHelper],
    props: {
        order: {
            type: Object,
            default: null
        },
        textColWidthPercentage: {
            type: Number,
            default: 40
        },
        invoiceStateOptions: {
            type: Array,
            default() {
                return []
            }
        },
        user: {
            type: Object,
            default: null
        },
        showAll: {
            type: Boolean,
            default: false
        },
        isSelected: {
            type: Boolean,
            default: false
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            itemsVisible: false,
            loading: 0,
            summaries: [],
            searchParams: null
        }
    },
    computed: {
        orderObject() {
            return this.order?.items?.[0]?.order ?? null
        },
        firstItem() {
            return this.order?.items?.[0] ?? null
        },
        rowCost() {
            return (this.order?.items || []).reduce((sum, i) => sum + ((i.invoiceable_sum == null || i.invoiceable_sum === 0) ? (i.cost ?? 0) : i.invoiceable_sum), 0)
        },
        rowAmount() {
            return (this.order?.items || []).reduce((sum, i) => sum + (i.invoiceable_amount ?? i.invoice_amount ?? 0), 0)
        },
        rowStartDate() {
            const dates = (this.order?.items || []).map(i => i.date).filter(Boolean)
            return dates.length ? dates.reduce((a, b) => a < b ? a : b) : null
        },
        rowEndDate() {
            const dates = (this.order?.items || []).map(i => i.date).filter(Boolean)
            return dates.length ? dates.reduce((a, b) => a > b ? a : b) : null
        },
    },
    watch: {
        itemsVisible() {
            if (this.itemsVisible) {
                this.showInvoiceSummary(null)
            }
        },
    },
    created: function () {
        if (this.showAll) {
            this.toggleItems()
        }
    },
    methods: {
        truncateInfo(info) {
            return info.length > 75 ? info.substring(0, 75) + '…' : info
        },
        toggleItems() {
            this.itemsVisible = !this.itemsVisible
        },
        orderUpdated() {
            this.$emit('orderUpdated')
        },
        fetchInvoiceRows() {
            this.loading = true
            this.restFetchParams(this.contractDailyTripSummaryUrl, this.searchParams, this.handleInvoiceRowsResponse, this.invoiceRowsFail)
        },
        handleInvoiceRowsResponse: function (response) {
            this.loading = false
            this.summaries = response.data
            if(this.summaries && this.summaries.length === 0) {
                this.showAlert(this.$t('common.error'))
            }
        },
        invoiceRowsFail: function () {
            this.loading = false
            this.summaries = []
            this.showAlert(this.$t('common.error'))
        },

        showInvoiceSummary(item) {
            const src = item?.order ? item : this.firstItem
            if (src) {
                this.searchParams = {
                    to: this.getDateAsString(new Date(src.end_date || this.rowEndDate)),
                    from: this.getDateAsString(new Date(src.start_date || this.rowStartDate)),
                    pricingModel: src.pricing_model?.id ?? null,
                    contractIds: [src.order?.contract?.id ?? this.firstItem?.order?.contract?.id],
                    orderDetails: 1,
                    orders: [src.order?.id ?? this.firstItem?.order?.id],
                    invoiceState: 2
                }
            }
            this.fetchInvoiceRows()
            this.$emit('updateSummaries')
        },
        collapseRow() {
            this.itemsVisible = false
        },
        canClose() {
            return !!this.itemsVisible;
        },
        emitTripDetails(row) {
            this.$emit('showTripDetails', row)
        },
        emitTripDeliveries(row) {
            this.$emit('showTripDeliveries', row)
        },
        emitTripOrder(row) {
            this.$emit('showTripOrder', row)
        },
        emitContractDetails(row) {
            this.$emit('showContractDetails', row)
        },
        emitInvoiceHistories(row) {
            this.$emit('showInvoiceHistories', row)
        },
        toggleSelection() {
            this.$emit('toggleSelection', this.orderObject?.id)
        }
    }
}
</script>
<style scoped>
 .order-text {
    display: inline-block;
    background: #ffffff;
    border-top: 1px #BCBCBC solid;
    margin: 0;
    padding: .2em;
    font-size: .9em;
    user-select: none;
    min-height: 2em;
}
.single-line {
    display: block;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.order-text:hover, .single-line:hover {
    color: #007BFF;
    font-weight: bold;
}
 .underline-text {
     text-decoration: underline;
     color: inherit; /* Keep the text color normal, optional */
     cursor: pointer; /* Already included by .pointer, but for clarity */
 }
</style>
